import React, {useEffect, useRef, useState} from 'react';
import {Auth, BrickContext, CloudObject, Color, Config} from 'olympe';
import {EZPRTheme} from '../_theme';
import {ShrunkenPREditor} from './ShrunkenPREditor.jsx';
import {ExpandedPREditor} from './ExpandedPREditor.jsx';
import PropTypes from 'prop-types';
import {Button, ThemeProvider} from '@mui/material';
import AppController, {AppStates} from '../../controllers/AppController';
import UserController from '../../controllers/UserController';
import {DenyPRPopup} from './dialogs/DenyPRPopup.jsx';
import {ErrorPopup} from './dialogs/ErrorPopup.jsx';
import {GenericPopup} from '../dialogs/GenericPopup.jsx';
import {requestStores} from '../ListOfPREditors.brick.jsx';
import {tagToDisplayER} from '../../utils/tagTransforms';
import {PurchaseRequest} from '../../models/PurchaseRequest';
import {SubmitController} from '../../controllers/SubmitController';
import {
    changeTabLambda,
    copyLambda,
    getCBJustificationThresholdLambda,
    getPRStatusFromBackendLambda,
    getPSLStatusLambda,
    getUserDefaultsLambda,
    getUserInfoByUserIDLambda,
    newSubmitPRLambda,
    saveLambda,
    sendEmailLambda,
    setToChangeLogLambda,
    setToChangeLogPromiseLambda,
    showPopupLambda,
} from '../../utils/functionSignatures';
import {ChangeLogPopup} from './dialogs/ChangeLogPopup.jsx';
import {createChangeLog, createCopyChangeLog, createSubmissionChangeLog} from '../../utils/changeLog';
import CustomPopup from '../dialogs/CustomPopup.jsx';
import {CurrencyController} from '../../controllers/CurrencyController';
import {PurchaseRequestController} from '../../controllers/PurchaseRequestController';
import {isValidFutureDate, materialDateIsNotPastDate} from '../../utils/date.js';
import {complianceIndicatorOptions, getUniquePrId} from '../_helpers.js';
import {getMailContentPRApproved, getMailContentPRSubmitted, getMailContentPRToRequester} from '../../utils/email';
import {verifySAPApiPermission} from '../../utils/verifySAPApiPermission';
import {createPRJson} from '../../utils/createPRJson';
import {Attachment} from '../../models/attachments_info/Attachment';
import {RejectPRPopup} from './dialogs/RejectPRPopup.jsx';
import {OneTimeVendor} from '../../models/OneTimeVendor';
import {isRequesterIncomplete, isSpenderIncomplete} from '../../utils/checkMandatoryFields';
import {normalizeCode, replaceJSX, showVendorDialog} from '../../utils/helpers';
import {InvalidPSLMaterialIdPopup} from '../dialogs/InvalidPSLMaterialIdPopup.jsx';
import {LAMBDA_NAMES, SOURCES} from '../../utils/constants';
import {ErrorBoundary} from 'react-error-boundary';
import {ExpandedPREditorFallBack} from './ExpandedPREditorFallback.jsx';
import {convertOldPRSourceToLabelValue} from '../../utils/sourceMappings';
import {isLabelValueEmpty} from '../../utils/checkObjects';

const READABLE_DATE_OPTIONS = {month: 'short', day: 'numeric', year: 'numeric'};

/**
 * @param {PREditor.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function PREditor(props) {
    const useRequestStore = requestStores.get(props.prTag);
    const [expanded, setExpanded] = useState(false);

    const [showDenyPRPopup, setShowDenyPRPopup] = useState(false);
    const [showRejectPRPopup, setShowRejectPRPopup] = useState(false);
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [showAPIErrorPopup, setShowAPIErrorPopup] = useState(false);
    const [showDateErrorPopup, setShowDateErrorPopup] = useState(false);
    const [dateErrorReason, setDateErrorReason] = useState('');
    const [errorReason, setErrorReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [disabledButtons, setDisabledButtons] = useState({
        save: false,
        submit: false,
        copy: false,
        approve: false,
        deny: false,
    });
    const [errorMessages, setErrorMessages] = useState([]);
    const [copySuccess, setCopySuccess] = useState(false);

    const [getStatus, setStatus] = useRequestStore((state) => [state.getStatus, state.setStatus]);
    const [showDenialReason, setShowDenialReason] = useState(false);
    const [showRejectionMsg, setShowRejectionMsg] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showChangeLog, setShowChangeLog] = useState(false);
    const [showConcurrencyPopup, setShowConcurrencyPopup] = useState(false);

    const [requestAlreadyWhat, setRequestAlreadyWhat] = useState('modified');

    const [isItemUngrouped, setIsItemUngrouped] = useState(false);

    const [showInvalidPSLMaterialIdPopup, setShowInvalidPSLMaterialIdPopup] = useState(false);
    const [invalidPSLMaterialIDMaterials, setInvalidPSLMaterialIDMaterials] = useState([]);
    const [invalidPSLMaterialIDAction, setInvalidPSLMaterialIDAction] = useState('');

    const save = props.context.get(LAMBDA_NAMES.SAVE);
    const copy = props.context.get(LAMBDA_NAMES.COPY);
    const showPopup = props.context.get(LAMBDA_NAMES.SHOW_POPUP);
    const changeTab = props.context.get(LAMBDA_NAMES.CHANGE_TAB);
    const getUserInfoByUserID = props.context.get(LAMBDA_NAMES.GET_USER_INFO_BY_USER_ID);
    const setToChangeLog = props.context.get(LAMBDA_NAMES.SET_TO_CHANGE_LOG);
    const newSubmitPR = props.context.get(LAMBDA_NAMES.NEW_SUBMIT_PR);
    const getCBJustificationThresholdBrick = props.context.get(LAMBDA_NAMES.GET_CB_JUSTIFICATION_THRESHOLD_TOLER);
    const sendEmail = props.context.get(LAMBDA_NAMES.SEND_EMAIL);
    const getPRStatusFromBackend = props.context.get(LAMBDA_NAMES.GET_PR_STATUS_FROM_BACKEND);
    const getPSLStatus = props.context.get(LAMBDA_NAMES.GET_PSL_STATUS);
    const materials = useRequestStore((state) => state.materials);
    const creationDate = useRequestStore((state) => state.creationDate);
    const selectedException = useRequestStore((state) => state.selectedException);

    const readableDate = creationDate.toLocaleString('en-US', READABLE_DATE_OPTIONS);

    const l1Category = Array.isArray(materials) && materials.length > 0 && materials[0].l1Category;
    const materialName = Array.isArray(materials) && materials.length > 0 && materials[0].name;

    const invoicePartner = useRequestStore((state) => state.invoicePartner);
    const areThereMultipleOrderingAddresses = useRequestStore((state) => state.areThereMultipleOrderingAddresses);
    const areThereMultipleInvoicePartners = useRequestStore((state) => state.areThereMultipleInvoicePartners);
    const showCBJustification = useRequestStore((state) => state.showCBJustification);
    const purchaseOrganization = useRequestStore((state) => state.purchaseOrganization);
    const OTVName = useRequestStore((state) => state.OTVName);
    const OTVAddress = useRequestStore((state) => state.OTVAddress);
    const supplierType = useRequestStore((state) => state.supplierType);
    const orderingAddress = useRequestStore((state) => state.orderingAddress);
    const operationalBuyers = useRequestStore((state) => state.operationalBuyers);
    const [denialInfo, setDenialInfo] = useRequestStore((state) => [state.denialInfo, state.setDenialInfo]);
    const vendorName = useRequestStore((state) => state.vendorName);
    const vendorCode = useRequestStore((state) => state.vendorCode);
    const getPurchaseRequestFromStore = useRequestStore((state) => state.getPurchaseRequest);
    const spender = useRequestStore((state) => state.spender);
    const requester = useRequestStore((state) => state.requester);
    const projectTitle = useRequestStore((state) => state.projectTitle);
    const rejectionMsg = useRequestStore((state) => state.rejectionMsg);
    const setUserIdToCallSAPAPIs = useRequestStore((state) => state.setUserIdToCallSAPAPIs);
    const userIdToCallSAPAPIs = useRequestStore((state) => state.userIdToCallSAPAPIs);
    const getNewPaymentTerms = props.context.get(LAMBDA_NAMES.GET_NEW_PAYMENT_TERMS);
    const newPaymentTerms = useRequestStore((state) => state.newPaymentTerms);
    const cbAttachments = useRequestStore((state) => state.competitiveBiddingJustificationAttachments);
    const wayOfBuying = useRequestStore((state) => state.wayOfBuying.currentValue);
    const purchasingGroup = useRequestStore((state) => state.purchasingGroup);
    const defaultPlant = useRequestStore((state) => state.defaultPlant);
    const defaultDeliveryAddress = useRequestStore((state) => state.defaultDeliveryAddress);
    const defaultRequestedDeliveryDate = useRequestStore((state) => state.defaultRequestedDeliveryDate);
    const [validityStart, validityEnd] = useRequestStore((state) => [state.validityStart, state.validityEnd]);
    const accounts = useRequestStore((state) => state.accounts);
    const accountDetailsByMaterial = useRequestStore((state) => state.accountDetailsByMaterial);
    const cleanAccountDetailsByMaterial = useRequestStore((state) => state.cleanAccountDetailsByMaterial);
    const areAccountSplitByMaterial = useRequestStore((state) => state.areAccountSplitByMaterial);
    const stopActionButton = useRequestStore((state) => state.stopActionButton);
    const hasException = useRequestStore((state) => state.hasException);
    const status = useRequestStore((state) => state.status);
    const supplierStatus = useRequestStore((state) => state.supplierStatus);
    const companyCodeCurrency = useRequestStore((state) => state.companyCodeCurrency);
    const getTotalPriceForBlankAndValidated = useRequestStore((state) => state.getTotalPriceForBlankAndValidated);
    const EPRId = useRequestStore((state) => state.EPRId);
    const mainAttachments = useRequestStore((state) => state.mainAttachments);
    const createCBAttachment = useRequestStore((state) => state.createCBAttachment);
    const deleteMainAttachment = useRequestStore((state) => state.deleteMainAttachment);
    const setSubmissionDate = useRequestStore((state) => state.setSubmissionDate);
    const clearDeletedItems = useRequestStore((state) => state.clearDeletedItems);
    const clearCopiedItems = useRequestStore((state) => state.clearCopiedItems);
    const [prSource, setPrSource] = useRequestStore((state) => [state.prSource, state.setPrSource]);
    const source = useRequestStore((state) => state.source);
    const sourceFromApiKey = useRequestStore((state) => state.sourceFromApiKey);
    const sourceToDisplay = useRequestStore((state) => state.sourceToDisplay);
    const mtvPowerBIValidated = useRequestStore((state) => state.mtvPowerBIValidated);

    const companyCode = useRequestStore((state) => state.companyCode);
    const strategicBuyers = useRequestStore((state) => state.strategicBuyers);
    const [sbDenialInfo, setSBDenialInfo] = useRequestStore((state) => [state.sbDenialInfo, state.setSBDenialInfo]);
    const setSBApprover = useRequestStore((state) => state.setSBApprover);

    const setConversionFailed = useRequestStore((state) => state.setConversionFailed);
    const setLastActiveUser = useRequestStore((state) => state.setLastActiveUser);
    const userDefaultsLambda = props.context.get(LAMBDA_NAMES.GET_USER_DEFAULT_INFO);

    const originalPr = props.context.get('Purchase Requests')
        .filter((o) => o instanceof CloudObject ? o.getTag() === props.prTag : o.tag === props.prTag)
        .map((obj) => JSON.parse(obj.json))[0];
    const currentPr = getPurchaseRequestFromStore().getPurchaseRequest();

    const isMounted = useRef(true);

    useEffect(() => {
        if (areAccountSplitByMaterial && accountDetailsByMaterial.some((a) => a.material_id === undefined)) {
            cleanAccountDetailsByMaterial();
        }

        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (expanded) {
            setShowDenialReason(getStatus() === PurchaseRequest.statuses.deniedByOB || getStatus() === PurchaseRequest.statuses.deniedBySB);
            setShowRejectionMsg(getStatus() === PurchaseRequest.statuses.deniedByRequester && !!rejectionMsg);
        }
        // Set to false when PR is shrunken. So next time user expands it, they can see errors again.
        if (!expanded) {
            setConversionFailed('');
        }
    }, [expanded]);

    // Switch to open requests tab after copying a PR from submitted requests.
    useEffect(() => {
        if (copySuccess) {
            changeTabLambda(props.context, changeTab, 'open requests', '', () => {});
            setCopySuccess(false);
        }
    }, [copySuccess]);

    try {
        const handleClickOnSupplier = (addPR) => {
            const openPrs = props.context.get('Open PRs');
            if ( addPR) {
                if (Array.isArray(openPrs)) {
                    openPrs.push(props.prTag);
                    props.context.set('Open PRs', [...openPrs]);
                } else {
                    props.context.set('Open PRs', [props.prTag]);
                }
                AppController.setNewAppState(AppStates.EDITING);
            } else {
                const index = openPrs.indexOf(props.prTag);
                openPrs.splice(index, 1);
                props.context.set('Open PRs', [...openPrs]);
                AppController.setNewAppState(AppStates.IDLE);
            }
        };
        const handleClickOnExpand = () => {
            const openPrs = props.context.get('Open PRs');

            // If we expand a PR, add it in the list of open PRs
            if (!expanded) {
                // if (systemAlias === null || systemAlias === undefined) {
                //     PurchaseRequestController.fetchSystemAlias(props.context, companyCode.value).then(setSystemAlias);
                // }
                if (Array.isArray(openPrs)) {
                    openPrs.push(props.prTag);
                    props.context.set('Open PRs', [...openPrs]);
                } else {
                    props.context.set('Open PRs', [props.prTag]);
                }
            // If we collapse a PR, remove it from the list of open PRs
            } else {
                const index = openPrs.indexOf(props.prTag);
                openPrs.splice(index, 1);
                props.context.set('Open PRs', [...openPrs]);
            }

            setExpanded(!expanded);
            if (expanded) {
                clearDeletedItems();
                clearCopiedItems();
                AppController.setNewAppState(AppStates.IDLE);
                if (isItemUngrouped) {
                    AppController.refreshPRsList();
                    setIsItemUngrouped(false);
                }
            } else {
                AppController.setNewAppState(AppStates.EDITING);
            }
        };

        const handleCloseErrorPopup = () => {
            setShowErrorPopup(false);
        };

        const handleClickOnDeny = async () => {
            setDisabledButtons({
                ...disabledButtons,
                deny: true,
            });

            const updatedStatus = await PurchaseRequestController.getUpdatedStatus(props.context, props.prTag);

            if (updatedStatus !== status) {
                if (updatedStatus === PurchaseRequest.statuses.incompleteSpender) {
                    setRequestAlreadyWhat('withdrawn');
                } else {
                    setRequestAlreadyWhat('modified');
                }
                setShowConcurrencyPopup(true);
                handleStopLoadings();
                return;
            }

            setShowDenyPRPopup(true);
        };

        const handleStopLoadings = () => {
            stopActionButton();
            if (!isMounted.current) return;
            setDisabledButtons({
                save: false,
                submit: false,
                copy: false,
                approve: false,
                deny: false,
            });
            setLoading(false);
        };

        const handleShowAPIErrorPopup = (context) => {
            if (!isMounted.current) return;
            setErrorReason(context);
            setShowAPIErrorPopup(true);
        };

        const handleDenyPopupClosed = (event, reason) => {
            // User canceled via outside click or Escape button
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                handleStopLoadings();
                setShowDenyPRPopup(false);
                return;
            }

            if (isLabelValueEmpty(prSource)) {
                setPrSource(convertOldPRSourceToLabelValue(source, sourceFromApiKey, sourceToDisplay));
            }

            let denialData;
            const currentUserId = UserController.getCurrentUserName();

            const env = Config.getParameter('ENV');
            const obTab = props.currentTab === PurchaseRequest.statuses.pendingNbs;
            const buyers = obTab ? operationalBuyers : strategicBuyers;
            const denialInfoSbOrOb = obTab ? denialInfo : sbDenialInfo;

            if (env === 'codev') {
                denialData = {
                    denialReason: denialInfoSbOrOb?.denialReason,
                    buyer: {
                        label: 'Jael Dubey',
                        value: currentUserId,
                        email: 'jael.dubey@olympe.io',
                    },
                };
            } else if (env === 'preprod') {
                const denier = buyers?.filter((buyer) => buyer.value.toUpperCase() === currentUserId);
                denialData = {
                    denialReason: denialInfoSbOrOb?.denialReason,
                    buyer: denier[0],
                };
            } else {
                const denier = buyers?.filter((buyer) => buyer.value.toUpperCase() === currentUserId);
                denialData = {
                    denialReason: denialInfoSbOrOb?.denialReason,
                    buyer: denier[0],
                };
            }

            SubmitController.denyRequest(
                props.context,
                requester,
                spender,
                props.prTag,
                denialInfoSbOrOb,
                readableDate,
                vendorCode,
                vendorName,
                l1Category,
                materialName,
                sendEmail,
                EPRId || tagToDisplayER(props.prTag),
            ).then(() => {
                obTab ? setDenialInfo(denialData) : setSBDenialInfo(denialData);
                setStatus(obTab ? PurchaseRequest.statuses.deniedByOB : PurchaseRequest.statuses.deniedBySB);
                expanded ? handleClickOnExpand() : null;
                saveLambda(props.context,
                    save,
                    createPRJson(getPurchaseRequestFromStore()),
                    props.prTag,
                    createChangeLog(originalPr, currentPr),
                    CloudObject.get(Auth.getCurrentUser()),
                    setToChangeLog,
                    () => {
                        clearDeletedItems();
                        clearCopiedItems();
                        handleStopLoadings();
                        setShowDenyPRPopup(false);
                        expanded ? handleClickOnExpand() : null;
                        props.context.trigger('Refresh');
                    },
                    () => handleStopLoadings(),
                    // If save fails.
                    () => {
                        handleStopLoadings();
                        handleShowAPIErrorPopup('Something went wrong while saving the PR (submit to requester).');
                    },
                    // If change log fails.
                    () => {
                        handleStopLoadings();
                        handleShowAPIErrorPopup('Something went wrong while creating the change log.');
                    });
            });
        };

        const handleRejectPopupClosed = (event, reason) => {
            // User canceled via outside click or Escape button
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                handleStopLoadings();
                setShowRejectPRPopup(false);
                return;
            }

            if (isLabelValueEmpty(prSource)) {
                setPrSource(convertOldPRSourceToLabelValue(source, sourceFromApiKey, sourceToDisplay));
            }

            SubmitController.rejectRequest(
                props.context,
                requester,
                spender,
                props.prTag,
                rejectionMsg,
                readableDate,
                vendorCode,
                vendorName,
                l1Category,
                materialName,
                sendEmail,
                EPRId,
            ).then(() => {
                setStatus(PurchaseRequest.statuses.deniedByRequester);
                expanded ? handleClickOnExpand() : null;
                saveLambda(props.context,
                    save,
                    createPRJson(getPurchaseRequestFromStore()),
                    props.prTag,
                    createChangeLog(originalPr, currentPr),
                    CloudObject.get(Auth.getCurrentUser()),
                    setToChangeLog,
                    () => {
                        clearDeletedItems();
                        clearCopiedItems();
                        handleStopLoadings();
                        setShowRejectPRPopup(false);
                        expanded ? handleClickOnExpand() : null;
                        props.context.trigger('Refresh');
                    },
                    () => handleStopLoadings(),
                    // If save fails.
                    () => {
                        handleStopLoadings();
                        handleShowAPIErrorPopup('Something went wrong while saving the PR (reject).');
                    },
                    // If change log fails.
                    () => {
                        handleStopLoadings();
                        handleShowAPIErrorPopup('Something went wrong while creating the change log.');
                    });
            });
        };

        const handleClickOnApprove = async (source) => {
            if (isSubmitting) {
                return;
            }

            if (isLabelValueEmpty(prSource)) {
                setPrSource(convertOldPRSourceToLabelValue(source, sourceFromApiKey, sourceToDisplay));
            }

            if (wayOfBuying.value === 'FO') {
                if (validityEnd && !isValidFutureDate(validityEnd)) {
                    handleStopLoadings();
                    setDateErrorReason('General Details: Check Validity end date as it cannot be in the past.');
                    setShowDateErrorPopup(true);
                    return;
                }
            } else {
                // check requested date
                if (defaultRequestedDeliveryDate && defaultRequestedDeliveryDate !== '' && !isValidFutureDate(defaultRequestedDeliveryDate)) {
                    handleStopLoadings();
                    setDateErrorReason('General Details: The delivery date needs to be changed as it cannot be in the past.');
                    setShowDateErrorPopup(true);
                    return;
                }
            }
            // check the materials date
            if (materials && !materialDateIsNotPastDate(materials)) {
                handleStopLoadings();
                setDateErrorReason('Item Details: The delivery date needs to be changed as it cannot be in the past.');
                setShowDateErrorPopup(true);
                return;
            }

            try {
                getPRStatusFromBackendLambda(props.context, getPRStatusFromBackend, props.prTag)
                    .then(async (statusFromBackend) => {
                        const updatedStatus = await PurchaseRequestController.getUpdatedStatus(props.context, props.prTag);
                        if (updatedStatus !== status || status !== statusFromBackend[0]) {
                            if (updatedStatus === PurchaseRequest.statuses.incompleteSpender) {
                                setRequestAlreadyWhat('withdrawn');
                            } else {
                                setRequestAlreadyWhat('modified');
                            }
                            setShowConcurrencyPopup(true);
                            handleStopLoadings();
                        } else {
                            setIsSubmitting(true);
                            // We need to set App state to Editing in order to not re-new the PR list and lose component.
                            // Related to subscribe method inside ListOfPREditors brick.
                            AppController.setNewAppState(AppStates.EDITING);
                            setDisabledButtons({
                                ...disabledButtons,
                                approve: true,
                            });
                            setLoading(true);

                            let isCBSectionDisplayed = showCBJustification;

                            if (source === PREditor.sourceOfEvent.SHRUNKEN_PR) {
                                verifySAPApiPermission(props.context, spender, requester, setUserIdToCallSAPAPIs, getNewPaymentTerms);

                                getCBJustificationThresholdLambda(
                                    props.context,
                                    getCBJustificationThresholdBrick,
                                    companyCode.value,
                                    supplierType === OneTimeVendor.supplierTypes.cmv || supplierType === OneTimeVendor.supplierTypes.otv || supplierType === OneTimeVendor.supplierTypes.mtv,
                                    userIdToCallSAPAPIs,
                                    async (resultThreshold) => {
                                        const parsedResultThreshold = JSON.parse(resultThreshold[0]);
                                        // If there is only one threshold and only one exchange rate, do the currency conversion
                                        if (parsedResultThreshold.length === 1) {
                                            const cbThreshold = Number(parsedResultThreshold[0].Net_Price);
                                            const cbCurrency = parsedResultThreshold[0].Currency;

                                            let totalPrice = getTotalPriceForBlankAndValidated();

                                            if (!!companyCodeCurrency && companyCodeCurrency !== cbCurrency) {
                                                const exchangeRate = await CurrencyController.getCurrencyExchangeRate(companyCodeCurrency, cbCurrency, userIdToCallSAPAPIs, props.context);
                                                totalPrice = totalPrice * exchangeRate.rate;
                                            }
                                            // If price is above the threshold and supplier status = Blank or Validated, display the CB Justification Threshold
                                            // Otherwise, do not display it
                                            isCBSectionDisplayed = (totalPrice >= cbThreshold) && (supplierStatus === 'Blank' || supplierStatus === 'Validated' || !supplierStatus);
                                        } else { // If there is no data (no threshold or no exchange rate), display the CB justification section
                                            isCBSectionDisplayed = supplierStatus === 'Blank' || supplierStatus === 'Validated' || !supplierStatus;
                                        }
                                    },
                                    () => {
                                        console.error();
                                    });
                            }

                            const pr = getPurchaseRequestFromStore().getPurchaseRequest();

                            // Add the SB approver to PR JSON
                            const currentUserFullName = localStorage.getItem('userFullName') || '';
                            if (props.currentTab === PurchaseRequest.tab.pendingSb) {
                                pr.sbValidation.sbApprover = currentUserFullName;
                                setSBApprover(currentUserFullName);
                            }

                            newSubmitPRLambda(props.context,
                                newSubmitPR,
                                JSON.stringify(pr),
                                props.prTag,
                                isCBSectionDisplayed,
                                props.currentTab === PurchaseRequest.tab.pendingSb,
                                false,
                                userIdToCallSAPAPIs,
                                async (sapPrNumber) => {
                                    await setToChangeLogPromiseLambda(
                                        props.context,
                                        setToChangeLog,
                                        createChangeLog(originalPr, getPurchaseRequestFromStore().getPurchaseRequest(), true),
                                        CloudObject.get(Auth.getCurrentUser()),
                                        props.prTag);

                                    const mailAddress = requester.email;
                                    const ccMailAddress = spender.email;
                                    const env = Config.getParameter('ENV');

                                    const mailContent = getMailContentPRApproved(requester,
                                        spender,
                                        readableDate,
                                        vendorCode,
                                        vendorName,
                                        l1Category,
                                        materialName,
                                        sapPrNumber,
                                        props.currentTab === PurchaseRequest.tab.pendingSb,
                                        props.prTag,
                                        EPRId || tagToDisplayER(props.prTag));
                                    sendEmailLambda(props.context, sendEmail, mailAddress, ccMailAddress, '', `[Easy Request] ${env !== 'prod' ? (env === 'codev' ? '[DEV] ' : '[PRE-PROD] ') : ''}PR ${sapPrNumber} approved`, mailContent, []).then(() => {
                                        setIsSubmitting(false);
                                        expanded ? handleClickOnExpand() : null;
                                        props.context.trigger('Refresh');
                                        handleStopLoadings();
                                    });
                                },
                                () => {
                                    if (props.currentTab === PurchaseRequest.tab.pendingSb) {
                                        setSBApprover('');
                                    }
                                    setIsSubmitting(false);
                                    handleStopLoadings();
                                });
                        }
                    })
                    .catch(() => {
                        setRequestAlreadyWhat('submitted');
                        setShowConcurrencyPopup(true);
                        handleStopLoadings();
                    });
            } catch (e) {
                setRequestAlreadyWhat('submitted');
                setShowConcurrencyPopup(true);
                handleStopLoadings();
            }
        };

        const handleClickOnSubmit = async (source) => {
            if (isSubmitting) {
                return;
            }

            if (isLabelValueEmpty(prSource)) {
                setPrSource(convertOldPRSourceToLabelValue(source, sourceFromApiKey, sourceToDisplay));
            }

            const pr = new PurchaseRequest(JSON.stringify(getPurchaseRequestFromStore().getPurchaseRequest()));

            if (pr.source.toUpperCase() === SOURCES.PSL || pr.source.toUpperCase() === SOURCES.MIXED.toUpperCase()) {
                const invalidMaterials = [];

                // Check each PSL Material ID is still active
                const uniqueSourcingToolIds = pr.materials
                    .map((material) => material.uniqueSourcingToolId)
                    .filter((id) => id);

                if (uniqueSourcingToolIds.length > 0) {
                    const statuses = await getPSLStatusLambda(props.context, getPSLStatus, uniqueSourcingToolIds);

                    const parsedStatuses = JSON.parse(statuses[0]);

                    if (parsedStatuses.data.some((d) => d['pslMaterailStatus'] !== 'Active')) {
                        const invalidMaterialsFromStatuses = pr.materials
                            .filter((material) => {
                                const statusEntry = parsedStatuses.data.find(
                                    (d) => d.pslMaterialId === material.uniqueSourcingToolId,
                                );
                                return statusEntry && statusEntry.pslMaterailStatus !== 'Active';
                            })
                            .map((material) => {
                                const statusEntry = parsedStatuses.data.find(
                                    (d) => d.pslMaterialId === material.uniqueSourcingToolId,
                                );
                                return {
                                    EPR: EPRId,
                                    line: (material._id + 1) * 10,
                                    material: normalizeCode(material._name.split(' - ')[0]),
                                    currentPSLStatus: statusEntry.pslMaterailStatus,
                                };
                            });

                        invalidMaterials.push(...invalidMaterialsFromStatuses);
                    }
                }

                if (invalidMaterials.length > 0) {
                    invalidMaterials.sort((a, b) => a.line - b.line);

                    setInvalidPSLMaterialIDMaterials(invalidMaterials);
                    setInvalidPSLMaterialIDAction('submitted');
                    setShowInvalidPSLMaterialIdPopup(true);
                    handleStopLoadings();
                    return;
                }
            }

            try {
                getPRStatusFromBackendLambda(props.context, getPRStatusFromBackend, props.prTag)
                    .then(async (statusFromBackend) => {
                        const updatedStatus = await PurchaseRequestController.getUpdatedStatus(props.context, props.prTag);
                        if (updatedStatus !== status || status !== statusFromBackend[0]) {
                            if (updatedStatus === PurchaseRequest.statuses.incompleteSpender) {
                                setRequestAlreadyWhat('withdrawn');
                            } else {
                                setRequestAlreadyWhat('modified');
                            }
                            setShowConcurrencyPopup(true);
                            handleStopLoadings();
                        } else {
                            setIsSubmitting(true);

                            // We need to set App state to Editing in order to not re-new the PR list and lose component.
                            // Related to subscribe method inside ListOfPREditors brick.
                            AppController.setNewAppState(AppStates.EDITING);
                            setLoading(true);
                            setDisabledButtons({
                                ...disabledButtons,
                                submit: true,
                            });

                            const validation = {
                                error: false,
                                errorMessages: [],
                            };
                            await SubmitController.checkMandatoryFields(
                                vendorCode,
                                wayOfBuying,
                                spender,
                                requester,
                                companyCode,
                                purchaseOrganization,
                                purchasingGroup,
                                defaultPlant,
                                defaultDeliveryAddress,
                                defaultRequestedDeliveryDate,
                                validityStart,
                                validityEnd,
                                materials,
                                accounts,
                                accountDetailsByMaterial,
                                areAccountSplitByMaterial,
                                validation,
                                OTVName,
                                OTVAddress,
                                supplierType,
                                hasException,
                                selectedException,
                                mainAttachments,
                                cbAttachments,
                                newPaymentTerms,
                                userIdToCallSAPAPIs,
                                props.context,
                                mtvPowerBIValidated,
                            );
                            if (validation.error) {
                                const errMsg = replaceJSX(validation.errorMessages, '<POP-UP>', <a href="#" onClick={(e)=> {
                                    showVendorDialog(e, props.prTag);
                                    handleCloseErrorPopup();
                                }}>pop-up</a> );
                                setErrorMessages(errMsg);
                                setShowErrorPopup(true);
                                setIsSubmitting(false);
                                handleStopLoadings();
                            } else {
                                let isCBSectionDisplayed = showCBJustification;

                                if (source === PREditor.sourceOfEvent.SHRUNKEN_PR) {
                                    verifySAPApiPermission(props.context, spender, requester, setUserIdToCallSAPAPIs, getNewPaymentTerms);

                                    getCBJustificationThresholdLambda(
                                        props.context,
                                        getCBJustificationThresholdBrick,
                                        companyCode.value,
                                        supplierType === OneTimeVendor.supplierTypes.cmv || supplierType === OneTimeVendor.supplierTypes.otv || supplierType === OneTimeVendor.supplierTypes.mtv,
                                        userIdToCallSAPAPIs,
                                        async (resultThreshold) => {
                                            const parsedResultThreshold = JSON.parse(resultThreshold[0]);
                                            let showCBSection;
                                            // If there is only one threshold and only one exchange rate, do the currency conversion
                                            if (parsedResultThreshold.length === 1) {
                                                const cbThreshold = Number(parsedResultThreshold[0].Net_Price);
                                                const cbCurrency = parsedResultThreshold[0].Currency;

                                                let totalPrice = getTotalPriceForBlankAndValidated();

                                                if (!!companyCodeCurrency && companyCodeCurrency !== cbCurrency) {
                                                    const exchangeRate = await CurrencyController.getCurrencyExchangeRate(companyCodeCurrency, cbCurrency, userIdToCallSAPAPIs, props.context);
                                                    totalPrice = totalPrice * exchangeRate.rate;
                                                }
                                                // If price is above the threshold and supplier status = Blank or Validated, display the CB Justification Threshold
                                                // Otherwise, do not display it
                                                showCBSection = (totalPrice >= cbThreshold) && (supplierStatus === 'Blank' || supplierStatus === 'Validated' || !supplierStatus);
                                            } else { // If there is no data (no threshold or no exchange rate), display the CB justification section
                                                showCBSection = supplierStatus === 'Blank' || supplierStatus === 'Validated' || !supplierStatus;
                                            }
                                            isCBSectionDisplayed = showCBSection;

                                            if (showCBSection) {
                                                const attachmentToMove = mainAttachments.find((a) => a.topic.toLowerCase() === 'event summary');
                                                if (attachmentToMove) {
                                                    createCBAttachment(new Attachment(attachmentToMove.file, 'Event summary', attachmentToMove.user, attachmentToMove.date, attachmentToMove.name, attachmentToMove.userId));
                                                    deleteMainAttachment(mainAttachments.findIndex((a) => a.topic.toLowerCase() === 'event summary'));
                                                }
                                            }
                                        },
                                        () => {
                                            console.error();
                                        });
                                }

                                const isThereAnEventSummary = cbAttachments.find((a) => a.topic === 'Event summary');
                                const isThereThreeOffers = cbAttachments.find((a) => a.topic === 'Offer no.1 (selected supplier)') &&
                                    cbAttachments.find((a) => a.topic === 'Offer no.2') &&
                                    cbAttachments.find((a) => a.topic === 'Offer no.3');
                                const isThereAnException = cbAttachments.find((a) => a.topic === 'Exception');
                                if (isCBSectionDisplayed) {
                                    if (hasException && !isThereAnException) {
                                        setErrorMessages(['You have to add at least one attachment under CB justification with "Exception" as the topic.']);
                                        setShowErrorPopup(true);
                                        setIsSubmitting(false);
                                        handleStopLoadings();
                                        return;
                                    } else if (!hasException && (!isThereAnEventSummary && !isThereThreeOffers)) {
                                        setErrorMessages(['You have to add at least one attachment under CB justification with "Event summary" as the topic, or three attachments with Offer no. 1, 2 and 3.']);
                                        setShowErrorPopup(true);
                                        setIsSubmitting(false);
                                        handleStopLoadings();
                                        return;
                                    }
                                }

                                newSubmitPRLambda(props.context,
                                    newSubmitPR,
                                    JSON.stringify(getPurchaseRequestFromStore().getPurchaseRequest()),
                                    props.prTag,
                                    isCBSectionDisplayed,
                                    false,
                                    false,
                                    userIdToCallSAPAPIs,
                                    async (sapPrNumber) => {
                                        const mailAddress = requester?.email;
                                        const ccMailAddress = spender?.email || '';
                                        const env = Config.getParameter('ENV');
                                        await setToChangeLogPromiseLambda(
                                            props.context,
                                            setToChangeLog,
                                            createSubmissionChangeLog(EPRId || tagToDisplayER(props.prTag)),
                                            CloudObject.get(Auth.getCurrentUser()),
                                            props.prTag);
                                        const mailContent = getMailContentPRSubmitted(requester,
                                            spender,
                                            readableDate,
                                            vendorCode,
                                            vendorName,
                                            l1Category,
                                            materialName,
                                            sapPrNumber,
                                            props.prTag,
                                            EPRId || tagToDisplayER(props.prTag));
                                        sendEmailLambda(props.context, sendEmail, mailAddress, ccMailAddress, '', `[Easy Request] ${env !== 'prod' ? (env === 'codev' ? '[DEV] ' : '[PRE-PROD] ') : ''}PR ${sapPrNumber} submitted`, mailContent, []).then(() => {
                                            setIsSubmitting(false);
                                            expanded ? handleClickOnExpand() : null;
                                            props.context.trigger('Refresh');
                                            handleStopLoadings();
                                        });
                                    },
                                    (e) => {
                                        console.error(e);
                                        setIsSubmitting(false);
                                        handleStopLoadings();
                                    });
                            }
                        }
                    })
                    .catch((e) => {
                        setRequestAlreadyWhat('submitted');
                        setShowConcurrencyPopup(true);
                        handleStopLoadings();
                    });
            } catch (e) {
                setRequestAlreadyWhat('submitted');
                setShowConcurrencyPopup(true);
                handleStopLoadings();
            }
        };

        const handleCloseDenialReason = () => {
            setShowDenialReason(false);
        };

        const handleCloseRejectionMsg = () => {
            setShowRejectionMsg(false);
        };

        const handleClickOnSubmitToRequester = async () => {
            const updatedStatus = await PurchaseRequestController.getUpdatedStatus(props.context, props.prTag);
            if (updatedStatus !== status) {
                if (updatedStatus === PurchaseRequest.statuses.incompleteSpender) {
                    setRequestAlreadyWhat('withdrawn');
                } else {
                    setRequestAlreadyWhat('modified');
                }
                setShowConcurrencyPopup(true);
                handleStopLoadings();
                return;
            }

            if (isLabelValueEmpty(prSource)) {
                setPrSource(convertOldPRSourceToLabelValue(source, sourceFromApiKey, sourceToDisplay));
            }

            setLoading(true);
            setDisabledButtons({
                ...disabledButtons,
                save: true,
            });
            const validation = {
                error: false,
                errorMessages: [],
            };
            SubmitController.checkMandatoryFieldsForSpender(
                vendorCode,
                wayOfBuying,
                spender,
                companyCode,
                purchaseOrganization,
                defaultPlant,
                defaultDeliveryAddress,
                defaultRequestedDeliveryDate,
                validityStart,
                validityEnd,
                materials,
                accounts,
                accountDetailsByMaterial,
                areAccountSplitByMaterial,
                validation,
                OTVName,
                OTVAddress,
                supplierType,
                hasException,
                selectedException,
                mainAttachments,
                cbAttachments,
                newPaymentTerms,
                mtvPowerBIValidated,
            );
            if (!validation.error) {
                const spenderIncomplete = isSpenderIncomplete(
                    spender,
                    materials,
                    accounts,
                    companyCode,
                    purchaseOrganization,
                    defaultPlant,
                    defaultDeliveryAddress,
                    defaultRequestedDeliveryDate,
                    validityStart,
                    validityEnd,
                    wayOfBuying,
                );

                const requesterIncomplete = isRequesterIncomplete(
                    requester,
                    wayOfBuying,
                    purchasingGroup,
                    materials,
                    vendorCode,
                    companyCode,
                );

                let newStatus;
                if (spenderIncomplete && requesterIncomplete) {
                    newStatus = PurchaseRequest.statuses.pendingRequesterAndSpenderCompletion;
                } else if (spenderIncomplete) {
                    newStatus = PurchaseRequest.statuses.pendingSpenderCompletion;
                } else if (requesterIncomplete) {
                    newStatus = PurchaseRequest.statuses.pendingRequesterCompletion;
                } else {
                    newStatus = PurchaseRequest.statuses.pendingRequesterCompletionSaved;
                }

                setStatus(newStatus);

                getUserInfoByUserIDLambda(
                    props.context,
                    getUserInfoByUserID,
                    UserController.getCurrentUserName(),
                    (userInfo) => {
                        const parsedUserInfo = JSON.parse(userInfo)[0];
                        setLastActiveUser(parsedUserInfo.description);
                        const prToSave = getPurchaseRequestFromStore().getPurchaseRequest();
                        prToSave.submissionDate = new Date().toUTCString();
                        const now = new Date();
                        const readableSubmissionDate = now.toLocaleDateString('en-US', READABLE_DATE_OPTIONS);
                        setSubmissionDate(readableSubmissionDate);
                        saveLambda(props.context,
                            save,
                            JSON.stringify(prToSave),
                            props.prTag,
                            createChangeLog(originalPr, currentPr),
                            CloudObject.get(Auth.getCurrentUser()),
                            setToChangeLog,
                            () => {
                                clearDeletedItems();
                                clearCopiedItems();
                                const mailContent = getMailContentPRToRequester(
                                    props.prTag,
                                    EPRId || tagToDisplayER(props.prTag),
                                    vendorCode,
                                    vendorName,
                                    projectTitle,
                                    readableSubmissionDate,
                                );
                                // If there is no email in the requester, do not send a notification
                                if (requester?.email) {
                                    const mailAddress = requester?.email;
                                    const ccMailAddress = spender?.email || '';
                                    const env = Config.getParameter('ENV');
                                    const spenderLabel = spender?.label ?? '';
                                    const vendorNameValue = vendorName ?? '';
                                    const subject = `[Easy Request] ${env !== 'prod' ? (env === 'codev' ? '[DEV] ' : '[PRE-PROD] ') : ''}PR to check! ${EPRId || tagToDisplayER(props.prTag)}${spenderLabel ? ` | ${spenderLabel}` : ''}${vendorNameValue ? ` | ${vendorNameValue}` : ''}`;

                                    sendEmailLambda(props.context, sendEmail, mailAddress, ccMailAddress, '', subject, mailContent, []).then(() => {
                                        expanded ? handleClickOnExpand() : null;
                                        props.context.trigger('Refresh');
                                    });
                                }
                            },
                            () => handleStopLoadings(),
                            // If save fails.
                            () => {
                                handleStopLoadings();
                                handleShowAPIErrorPopup('Something went wrong while saving the PR (submit to requester).');
                            },
                            // If change log fails.
                            () => {
                                handleStopLoadings();
                                handleShowAPIErrorPopup('Something went wrong while creating the change log.');
                            },
                        );
                    },
                    () => {
                        handleStopLoadings();
                        handleShowAPIErrorPopup('Something went wrong while fetching user data.');
                    },
                );
            } else {
                const errMsg = replaceJSX(validation.errorMessages, '<POP-UP>', <a href="#" onClick={(e)=> {
                    showVendorDialog(e, props.prTag);
                    handleCloseErrorPopup();
                }}>pop-up</a> );
                handleStopLoadings();
                setErrorMessages(errMsg);
                setShowErrorPopup(true);
            }
        };

        const handleClickOnWithdrawn = async () => {
            const updatedStatus = await PurchaseRequestController.getUpdatedStatus(props.context, props.prTag);
            if (updatedStatus !== status) {
                if ((updatedStatus === PurchaseRequest.statuses.incompleteSpender) || (updatedStatus === PurchaseRequest.statuses.incompleteRequester)) {
                    setRequestAlreadyWhat('withdrawn');
                } else {
                    setRequestAlreadyWhat('modified');
                }
                setShowConcurrencyPopup(true);
                handleStopLoadings();
                return;
            }
            setLoading(true);
            setDisabledButtons({
                ...disabledButtons,
                save: true,
            });

            if (isLabelValueEmpty(prSource)) {
                setPrSource(convertOldPRSourceToLabelValue(source, sourceFromApiKey, sourceToDisplay));
            }

            setStatus(PurchaseRequestController.saveBtnAssignNewStatus(false,
                spender,
                requester,
                wayOfBuying,
                purchasingGroup,
                areThereMultipleOrderingAddresses,
                areThereMultipleInvoicePartners,
                orderingAddress,
                invoicePartner,
                materials,
                showCBJustification,
                cbAttachments,
                status,
                companyCode,
                purchaseOrganization,
                defaultPlant,
                defaultDeliveryAddress,
                defaultRequestedDeliveryDate,
                validityStart,
                validityEnd,
                accounts,
                vendorCode));

            getUserInfoByUserIDLambda(
                props.context,
                getUserInfoByUserID,
                UserController.getCurrentUserName(),
                (userInfo) => {
                    const parsedUserInfo = JSON.parse(userInfo)[0];
                    setLastActiveUser(parsedUserInfo.description);
                    const prToSave = getPurchaseRequestFromStore().getPurchaseRequest();
                    prToSave.submissionDate = '';
                    setSubmissionDate('');
                    saveLambda(props.context,
                        save,
                        JSON.stringify(prToSave),
                        props.prTag,
                        createChangeLog(originalPr, currentPr),
                        CloudObject.get(Auth.getCurrentUser()),
                        setToChangeLog,
                        () => {
                            clearDeletedItems();
                            clearCopiedItems();
                            expanded ? handleClickOnExpand() : null;
                            props.context.trigger('Refresh');
                        },
                        () => handleStopLoadings(),
                        // If save fails.
                        () => {
                            handleStopLoadings();
                            handleShowAPIErrorPopup('Something went wrong while saving the PR (submit to requester).');
                        },
                        // If change log fails.
                        () => {
                            handleStopLoadings();
                            handleShowAPIErrorPopup('Something went wrong while creating the change log.');
                        },
                    );
                },
                () => {
                    handleStopLoadings();
                    handleShowAPIErrorPopup('Something went wrong while fetching user data.');
                },
            );
        };

        const handleClickOnSave = async () => {
            setLoading(true);

            const isFailedPR = props.failedMassSubmittedPRs.some((pr) => pr.prTag === props.prTag);

            if (isFailedPR) {
                props.setFailedMassSubmittedPRs((prevFailedPRs) => {
                    const updatedFailedPRs = prevFailedPRs.filter((pr) => pr.prTag !== props.prTag);
                    props.context.set('Failed PRs', updatedFailedPRs);
                    return updatedFailedPRs;
                });
            }

            setDisabledButtons({
                ...disabledButtons,
                save: true,
            });

            const updatedStatus = await PurchaseRequestController.getUpdatedStatus(props.context, props.prTag);

            if (updatedStatus !== status) {
                if (updatedStatus === PurchaseRequest.statuses.incompleteSpender) {
                    setRequestAlreadyWhat('withdrawn');
                } else {
                    setRequestAlreadyWhat('modified');
                }
                setShowConcurrencyPopup(true);
                handleStopLoadings();
                return;
            }

            setStatus(PurchaseRequestController.saveBtnAssignNewStatus(true,
                spender,
                requester,
                wayOfBuying,
                purchasingGroup,
                areThereMultipleOrderingAddresses,
                areThereMultipleInvoicePartners,
                orderingAddress,
                invoicePartner,
                materials,
                showCBJustification,
                cbAttachments,
                status,
                companyCode,
                purchaseOrganization,
                defaultPlant,
                defaultDeliveryAddress,
                defaultRequestedDeliveryDate,
                validityStart,
                validityEnd,
                accounts,
                vendorCode));

            if (isLabelValueEmpty(prSource)) {
                setPrSource(convertOldPRSourceToLabelValue(source, sourceFromApiKey, sourceToDisplay));
            }

            const pr = getPurchaseRequestFromStore().getPurchaseRequest();
            pr.rejectionMsg = '';

            getUserInfoByUserIDLambda(
                props.context,
                getUserInfoByUserID,
                UserController.getCurrentUserName(),
                (userInfo) => {
                    const parsedUserInfo = JSON.parse(userInfo)[0];
                    setLastActiveUser(parsedUserInfo.description);
                    saveLambda(props.context,
                        save,
                        JSON.stringify(pr),
                        props.prTag,
                        createChangeLog(originalPr, currentPr),
                        CloudObject.get(Auth.getCurrentUser()),
                        setToChangeLog,
                        () => {
                            clearDeletedItems();
                            clearCopiedItems();
                            expanded ? handleClickOnExpand() : null;
                            props.context.trigger('Refresh');
                        },
                        () => handleStopLoadings(),
                        // If save fails.
                        () => {
                            handleStopLoadings();
                            handleShowAPIErrorPopup('Something went wrong while saving the PR.');
                        },
                        // If change log fails.
                        () => {
                            handleStopLoadings();
                            handleShowAPIErrorPopup('Something went wrong while creating the change log.');
                        },
                    );
                },
                () => {
                    handleStopLoadings();
                    handleShowAPIErrorPopup('Something went wrong while fetching user data.');
                },
            );
        };

        const handleClickOnCopy = async () => {
            setDisabledButtons({
                ...disabledButtons,
                copy: true,
            });
            setLoading(true);

            if (isLabelValueEmpty(prSource)) {
                setPrSource(convertOldPRSourceToLabelValue(source, sourceFromApiKey, sourceToDisplay));
            }

            // first check if the PR is from PSL, and we have a PSL ID, then the PSL status
            const pr = new PurchaseRequest(JSON.stringify(getPurchaseRequestFromStore().getPurchaseRequest()));

            const isPSL = (pr.source && (pr.source.toUpperCase() === SOURCES.PSL || pr.source.toUpperCase() === SOURCES.MIXED.toUpperCase())) ||
                (pr.sourceFromApiKey && (pr.sourceFromApiKey.toUpperCase() === SOURCES.PSL || pr.sourceFromApiKey.toUpperCase() === SOURCES.MIXED.toUpperCase()));
            if (isPSL) {
                const hasPSLMaterialId = pr.materials.every((material) => material.uniqueSourcingToolId);
                if (!hasPSLMaterialId) {
                    showPopupLambda(
                        props.context,
                        showPopup,
                        'Your request has not been copied.',
                        `Since there is no PSL Material Id for this PR, the PR cannot be copied. Please use the PSL to initiate a new PR.`,
                        Color.create(241, 144, 2),
                        'content_copy',
                        () => {
                            handleStopLoadings();
                        },
                    );
                    return;
                }

                // check if the material has PSL ID
                const hasPSLId = pr.materials.every((material) => {
                    return !!material.pslId;
                });
                if (!hasPSLId) {
                    showPopupLambda(props.context, showPopup, 'Your request has not been copied.', `Since there is no PSL Id for this PR, the PR cannot be copied. Please use the PSL to initiate a new PR.`, Color.create(241, 144, 2), 'content_copy', () => {
                        handleStopLoadings();
                    });
                    return;
                }

                const invalidMaterials = [];

                // Check each PSL Material ID is still active
                const uniqueSourcingToolIds = pr.materials
                    .map((material) => material.uniqueSourcingToolId)
                    .filter((id) => id);

                if (uniqueSourcingToolIds.length > 0) {
                    const statuses = await getPSLStatusLambda(props.context, getPSLStatus, uniqueSourcingToolIds);

                    const parsedStatuses = JSON.parse(statuses[0]);

                    if (parsedStatuses.data.some((d) => d['pslMaterailStatus'] !== 'Active')) {
                        const invalidMaterialsFromStatuses = pr.materials
                            .filter((material) => {
                                const statusEntry = parsedStatuses.data.find(
                                    (d) => d.pslMaterialId === material.uniqueSourcingToolId,
                                );
                                return statusEntry && statusEntry.pslMaterailStatus !== 'Active';
                            })
                            .map((material) => {
                                const statusEntry = parsedStatuses.data.find(
                                    (d) => d.pslMaterialId === material.uniqueSourcingToolId,
                                );
                                return {
                                    EPR: EPRId,
                                    line: (material._id + 1) * 10,
                                    material: normalizeCode(material._name.split(' - ')[0]),
                                    currentPSLStatus: statusEntry.pslMaterailStatus,
                                };
                            });

                        invalidMaterials.push(...invalidMaterialsFromStatuses);
                    }
                }

                if (invalidMaterials.length > 0) {
                    invalidMaterials.sort((a, b) => a.line - b.line);

                    setInvalidPSLMaterialIDMaterials(invalidMaterials);
                    setInvalidPSLMaterialIDAction('copied');
                    setShowInvalidPSLMaterialIdPopup(true);
                    handleStopLoadings();
                    return;
                }
            }

            const userID = UserController.getCurrentUserName('EZPR-USER');

            let uniquePrId;
            try {
                uniquePrId = await getUniquePrId(props.context);
            } catch (error) {
                handleStopLoadings();
                handleShowAPIErrorPopup('Something went wrong while fetching the new EPRId.');
                return;
            }
            getUserInfoByUserIDLambda(
                props.context,
                getUserInfoByUserID,
                userID,
                (userInfo) => {
                    getUserDefaultsLambda(
                        props.context,
                        userDefaultsLambda,
                        (userDefaults) => {
                            pr.projectTitle = '';
                            pr.prNumber = '';
                            pr.spender = userDefaults.spender?.value ? {...userDefaults.spender} : {
                                value: '',
                                label: '',
                                email: '',
                            };
                            pr.requester = userDefaults.requester?.value ? {...userDefaults.requester} : {
                                value: '',
                                label: '',
                                email: '',
                            };
                            pr.creationDate = new Date().toUTCString();
                            pr.attachmentsInfo.competitiveBiddingJustificationAttachments = [];
                            pr.attachmentsInfo.mainAttachments = [];
                            pr.attachmentsInfo.complianceIndicator = complianceIndicatorOptions[0];
                            pr.attachmentsInfo.exception = false;
                            pr.attachmentsInfo.selectedException = '';
                            pr.status = PurchaseRequest.statuses.created;
                            pr.headerDetails.validityStart = '';
                            pr.headerDetails.validityEnd = '';
                            pr.headerDetails.defaultRequestedDeliveryDate = '';
                            pr.materials.forEach((material) => material.requestedDeliveryDate = '');
                            pr.submissionDate = '';
                            pr.EPRId = uniquePrId;
                            pr.sbValidation.sbApprover = '';
                            pr.nbsValidation.obApprover = '';
                            if (pr.sendingOptions.otherAddress && pr.sendingOptions.otherAddress !== '') {
                                // preparing to remove it also trim the internalComment
                                const otherAddress = pr.sendingOptions.otherAddress.split(';').join('; ');
                                pr.sendingOptions.otherAddress = '';
                                pr.sendingOptions.p2pOther = false;
                                pr.internalComment = pr.internalComment.replace(otherAddress, '');
                            }
                            if (pr.sendingOptions.supplierAddresses && pr.sendingOptions.supplierAddresses.length > 0 ) {
                                // preparing to remove it also trim the internalComment
                                const otherAddress = pr.sendingOptions.supplierAddresses.join('; ');
                                pr.sendingOptions.p2pSupplier = false;
                                pr.sendingOptions.vmdSupplier = false;
                                pr.sendingOptions.pslSupplier = false;
                                pr.sendingOptions.supplierAddresses = [];
                                pr.internalComment = pr.internalComment.replace(otherAddress, '');
                            }
                            copyLambda(
                                props.context,
                                copy,
                                JSON.stringify(pr.getPurchaseRequest()),
                                (tag) => {
                                    setToChangeLogLambda(
                                        props.context,
                                        setToChangeLog,
                                        createCopyChangeLog(currentPr.EPRId, uniquePrId),
                                        CloudObject.get(Auth.getCurrentUser()),
                                        tag,
                                        () => {
                                            handleStopLoadings();
                                            showPopupLambda(props.context, showPopup, 'Your request has been copied.', `This is the ID of your new PR: EPR-${pr.EPRId || tagToDisplayER(tag)}`, Color.create(0, 128, 0), 'content_copy', () => {
                                                setCopySuccess(true);
                                            });
                                            props.context.trigger('Refresh');
                                        },
                                        () => {
                                            handleStopLoadings();
                                            handleShowAPIErrorPopup('Something went wrong while creating the copied PR\'s change log' );
                                        });
                                },
                                () => {
                                    handleStopLoadings();
                                    handleShowAPIErrorPopup('Something went wrong while copying the PR.');
                                },
                            );
                        },
                        () => {
                            handleStopLoadings();
                            handleShowAPIErrorPopup('Something went wrong while fetching user defaults data.');
                        },
                    );
                },
                () => {
                    handleStopLoadings();
                    handleShowAPIErrorPopup('Something went wrong while fetching user data.');
                },
            );
        };

        const handleClickOnReject = async () => {
            setDisabledButtons({
                ...disabledButtons,
                save: true,
            });

            const updatedStatus = await PurchaseRequestController.getUpdatedStatus(props.context, props.prTag);

            if (updatedStatus !== status) {
                if (updatedStatus === PurchaseRequest.statuses.incompleteSpender) {
                    setRequestAlreadyWhat('withdrawn');
                } else {
                    setRequestAlreadyWhat('modified');
                }
                setShowConcurrencyPopup(true);
                handleStopLoadings();
                return;
            }

            setShowRejectPRPopup(true);
        };

        const handleClickOnChangeLog = () => {
            setShowChangeLog(true);
        };

        const handleCloseChangeLog = () => {
            setShowChangeLog(false);
        };

        const handleCloseAndReloadConcurrencyPopup = () => {
            window.location.reload();
        };

        const handleOnCloseInvalidPSLMaterialIdPopup = () => {
            setShowInvalidPSLMaterialIdPopup(false);
            setInvalidPSLMaterialIDMaterials([]);
            setInvalidPSLMaterialIDAction('');
        };

        return (
            <ThemeProvider theme={EZPRTheme}>
                {expanded ?
                    <ErrorBoundary
                        fallbackRender={({error, resetErrorBoundary}) => {
                            if (error.message?.includes('removeChild')) {
                                return null;
                            }
                            return (
                                <ExpandedPREditorFallBack
                                    handleClickOnExpand={handleClickOnExpand}
                                    onSave={handleClickOnSave}
                                    onSubmit={() => handleClickOnSubmit(PREditor.sourceOfEvent.SHRUNKEN_PR)}
                                    context={props.context}
                                    prTag={props.prTag}
                                    handleClickOnApprove={() => handleClickOnApprove(PREditor.sourceOfEvent.SHRUNKEN_PR)}
                                    handleClickOnCopy={handleClickOnCopy}
                                    handleClickOnChangeLog={handleClickOnChangeLog}
                                    handleClickOnDeny={handleClickOnDeny}
                                    handleClickOnSubmitToRequester={handleClickOnSubmitToRequester}
                                    handleClickOnWithdrawn={handleClickOnWithdrawn}
                                    handleClickOnReject={handleClickOnReject}
                                    loading={loading}
                                    disabledButtons={disabledButtons}
                                    prIdFromQueryParam={props.prIdFromQueryParam}
                                    removePrIdFromRef={props.removePrIdFromRef}
                                    selectedPRs={props.selectedPRs}
                                    selectPRsMode={props.selectPRsMode}
                                    setSelectedPRs={props.setSelectedPRs}
                                    failedMassSubmittedPRs={props.failedMassSubmittedPRs}
                                    setFailedMassSubmittedPRs={props.setFailedMassSubmittedPRs}
                                    setSelectPRsMode={props.setSelectPRsMode}
                                    isAnyPRSelected={props.isAnyPRSelected}
                                    handleMassSubmit={props.handleMassSubmit}
                                    massSubmitLoading={props.massSubmitLoading}
                                    massDeleteLoading={props.massDeleteLoading}
                                    handleMassDelete={props.handleMassDelete}
                                    massCopyLoading={props.massCopyLoading}
                                    handleMassCopy={props.handleMassCopy}
                                    handleClickOnSupplier={handleClickOnSupplier}
                                    errorMessage={error?.message}
                                />
                            );
                        }}
                    >
                        <ExpandedPREditor
                            context={props.context}
                            prTag={props.prTag}
                            handleClickOnExpand={handleClickOnExpand}
                            handleClickOnApprove={() => handleClickOnApprove(PREditor.sourceOfEvent.OPEN_PR)}
                            handleClickOnDeny={handleClickOnDeny}
                            handleClickOnSave={handleClickOnSave}
                            handleClickOnSubmit={() => handleClickOnSubmit(PREditor.sourceOfEvent.OPEN_PR)}
                            handleClickOnCopy={handleClickOnCopy}
                            handleClickOnSubmitToRequester={handleClickOnSubmitToRequester}
                            handleClickOnWithdrawn={handleClickOnWithdrawn}
                            handleClickOnReject={handleClickOnReject}
                            loading={loading}
                            disabledButtons={disabledButtons}
                            setDisabledButtons={setDisabledButtons}
                            usersCheckingThePr={props.usersCheckingThePr}
                            setIsItemUngrouped={setIsItemUngrouped}
                        />
                    </ErrorBoundary>:
                    <ShrunkenPREditor
                        rowIndex={props.rowIndex}
                        handleClickOnExpand={handleClickOnExpand}
                        onSave={handleClickOnSave}
                        onSubmit={() => handleClickOnSubmit(PREditor.sourceOfEvent.SHRUNKEN_PR)}
                        context={props.context}
                        prTag={props.prTag}
                        handleClickOnApprove={() => handleClickOnApprove(PREditor.sourceOfEvent.SHRUNKEN_PR)}
                        handleClickOnCopy={handleClickOnCopy}
                        handleClickOnChangeLog={handleClickOnChangeLog}
                        handleClickOnDeny={handleClickOnDeny}
                        handleClickOnSubmitToRequester={handleClickOnSubmitToRequester}
                        handleClickOnWithdrawn={handleClickOnWithdrawn}
                        handleClickOnReject={handleClickOnReject}
                        loading={loading}
                        disabledButtons={disabledButtons}
                        prIdFromQueryParam={props.prIdFromQueryParam}
                        removePrIdFromRef={props.removePrIdFromRef}
                        selectedPRs={props.selectedPRs}
                        selectPRsMode={props.selectPRsMode}
                        setSelectedPRs={props.setSelectedPRs}
                        failedMassSubmittedPRs={props.failedMassSubmittedPRs}
                        setFailedMassSubmittedPRs={props.setFailedMassSubmittedPRs}
                        setSelectPRsMode={props.setSelectPRsMode}
                        isAnyPRSelected={props.isAnyPRSelected}
                        handleMassSubmit={props.handleMassSubmit}
                        massSubmitLoading={props.massSubmitLoading}
                        massDeleteLoading={props.massDeleteLoading}
                        handleMassDelete={props.handleMassDelete}
                        massCopyLoading={props.massCopyLoading}
                        handleMassCopy={props.handleMassCopy}
                        handleClickOnSupplier={handleClickOnSupplier}
                    />
                }
                <DenyPRPopup
                    onClose={handleDenyPopupClosed}
                    onCancel={() => {
                        setDisabledButtons({
                            ...disabledButtons,
                            deny: false,
                        });
                        setShowDenyPRPopup(false);
                    }}
                    open={showDenyPRPopup}
                    prTag={props.prTag}
                    currentTab={props.currentTab}
                />
                <RejectPRPopup
                    onClose={handleRejectPopupClosed}
                    onCancel={() => {
                        setDisabledButtons({
                            ...disabledButtons,
                            save: false,
                        });
                        setShowRejectPRPopup(false);
                    }}
                    open={showRejectPRPopup}
                    prTag={props.prTag}
                />
                <ErrorPopup
                    onClose={handleCloseErrorPopup}
                    open={showErrorPopup}
                    errorMessages={errorMessages}
                />
                <GenericPopup
                    open={showDenialReason}
                    title={`Your request has been denied by the ${getStatus() === PurchaseRequest.statuses.deniedByOB ? 'Operational Buyer' : 'Strategic Buyer'}`}
                    text={`Here is the reason of the denial: ${getStatus() === PurchaseRequest.statuses.deniedByOB ? denialInfo?.denialReason : sbDenialInfo?.denialReason}`}
                    color={'#FF0000'}
                    icon={'error'}
                    onClose={handleCloseDenialReason}
                    buttons={[{
                        text: 'Ok',
                        handleFunction: handleCloseDenialReason,
                    }]}
                />
                <GenericPopup
                    open={showRejectionMsg}
                    title={`Your request has been rejected by the requester`}
                    text={`Here is the reason of the rejection: ${rejectionMsg}`}
                    color={'#FF0000'}
                    icon={'error'}
                    onClose={handleCloseRejectionMsg}
                    buttons={[{
                        text: 'Ok',
                        handleFunction: handleCloseRejectionMsg,
                    }]}
                />
                <ChangeLogPopup
                    onClose={handleCloseChangeLog}
                    open={showChangeLog}
                    prTag={props.prTag}
                    context={props.context}
                />
                <CustomPopup
                    title="Error"
                    content={errorReason}
                    buttons={[
                        <Button
                            key={0}
                            onClick={() => {
                                setErrorReason('');
                                setShowAPIErrorPopup(false);
                            }}
                            autoFocus
                        >
                            Ok
                        </Button>,
                    ]}
                    open={showAPIErrorPopup}
                    handleClose={() => {
                        setErrorReason('');
                        setShowAPIErrorPopup(false);
                    }}
                    showErrorIcon={true}
                />
                <CustomPopup
                    title="Error"
                    content={dateErrorReason}
                    buttons={[
                        <Button
                            key={0}
                            onClick={() => {
                                setDateErrorReason('');
                                setShowDateErrorPopup(false);
                            }}
                            autoFocus
                        >
                            Ok
                        </Button>,
                    ]}
                    open={showDateErrorPopup}
                    handleClose={() => {
                        setDateErrorReason('');
                        setShowDateErrorPopup(false);
                    }}
                    showErrorIcon={true}
                />
                <GenericPopup
                    open={showConcurrencyPopup}
                    onClose={handleCloseAndReloadConcurrencyPopup}
                    title={`Request is already ${requestAlreadyWhat}!`}
                    text={`This request has already been ${requestAlreadyWhat} while you were reviewing it. Therefore, your action cannot be completed.`}
                    icon={'sync_problem'}
                    color={'#458CEB'}
                    buttons={[{
                        text: 'Reload',
                        handleFunction: handleCloseAndReloadConcurrencyPopup,
                    }]}
                />
                <InvalidPSLMaterialIdPopup
                    open={showInvalidPSLMaterialIdPopup}
                    materials={invalidPSLMaterialIDMaterials}
                    typeOfAction={invalidPSLMaterialIDAction}
                    multipleRequests={false}
                    onClose={handleOnCloseInvalidPSLMaterialIdPopup}
                />
            </ThemeProvider>
        );
    } catch (e) {
        console.error(e);
        return null;
    }
}

/**
 * @enum {string}
 */
PREditor.sourceOfEvent = {
    SHRUNKEN_PR: 'shrunkenPr',
    OPEN_PR: 'openPr',
};

PREditor.propTypes = {
    rowIndex: PropTypes.number,
    prTag: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    currentTab: PropTypes.string,
    usersCheckingThePr: PropTypes.array,
    selectedPRs: PropTypes.array,
    setSelectedPRs: PropTypes.func,
    selectPRsMode: PropTypes.bool,
    setSelectPRsMode: PropTypes.func,
    failedMassSubmittedPRs: PropTypes.array,
    setFailedMassSubmittedPRs: PropTypes.func,
    isAnyPRSelected: PropTypes.bool,
    handleMassSubmit: PropTypes.func,
    massSubmitLoading: PropTypes.bool,
    massDeleteLoading: PropTypes.bool,
    handleMassDelete: PropTypes.func,
    massCopyLoading: PropTypes.bool,
    handleMassCopy: PropTypes.func,
};
